/**
 * Şablonlar — müşteriye ve firmaya giden mail/SMS metinleri.
 *
 * Tasarım kararları:
 *
 * 1. **Yazarken önizleme.** Metin değiştikçe (300 ms gecikmeyle) sunucu örnek
 *    veriyle doldurur ve geri gönderir. Kullanıcı "kaydet, gönder, bak" döngüsü
 *    yaşamasın; SMS'te kaç parça gideceğini ANINDA görsün.
 *
 * 2. **Yer tutucular tıklanır çip.** `{{musteriAdi}}` yazımını ezberletmek
 *    yerine çipe tıklayınca imlece eklenir. Bilinmeyen yer tutucu sunucuda
 *    hata olur; çipler o hatayı baştan önler.
 *
 * 3. **Test gönderimi kaydedilmiş hâli gönderir.** Kaydedilmemiş metinle test
 *    istenirse önce kaydetmesi söylenir — motor tek kaynaktan okur, panel
 *    ikinci bir yol açmaz.
 *
 * 4. **Varsayılana dön** ayrı düğme: bozulan şablonu kurtarma yolu her zaman var.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, MessageSquare, RotateCcw, Save, Send } from 'lucide-react';
import { api, type Sablon, type SablonOnizleme } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart, KartBasligi, KartGovdesi } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { Girdi, CokSatir } from '../components/ui/Girdi';
import { Pencere } from '../components/ui/Pencere';
import { DurumRozeti } from '../components/DurumRozeti';
import { HataKutusu, Yukleniyor } from '../components/Durumlar';
import { basariBildir, bilgiBildir, hataBildir, onayIste } from '../lib/bildirim';
import { tarihSaatYaz } from '../lib/format';
import { useOturum } from '../oturum';
import { izinVar } from '../lib/yetki';

export function Sablonlar() {
  const { kullanici } = useOturum();
  const { data, error, isPending, refetch } = useQuery({ queryKey: ['sablonlar'], queryFn: api.sablonlar });
  const [acik, setAcik] = useState<Sablon | null>(null);
  const duzenleyebilir = izinVar(kullanici, 'sablon.duzenle');

  return (
    <Kabuk
      baslik="Şablonlar"
      kirintilar={[{ etiket: 'Yönetim' }, { etiket: 'Şablonlar' }]}
      altBaslik="Müşteriye ve firmaya giden e-posta / SMS metinleri"
    >
      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}
      {isPending ? (
        <Kart>
          <Yukleniyor satir={4} />
        </Kart>
      ) : (
        <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2">
          {data?.items.map((s) => (
            <Kart key={s.key}>
              <KartBasligi
                baslik={s.ad}
                aciklama={s.aciklama}
                eylemler={
                  <DurumRozeti
                    ton={s.kanal === 'MAIL' ? 'info' : 'neutral'}
                    etiket={s.kanal === 'MAIL' ? 'E-posta' : 'SMS'}
                  />
                }
              />
              <KartGovdesi className="flex flex-col gap-3">
                {s.kanal === 'MAIL' && (
                  <p className="truncate text-sm font-medium text-[var(--ink)]">{s.konu}</p>
                )}
                <pre className="line-clamp-4 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3 text-xs whitespace-pre-wrap text-[var(--ink-muted)]">
                  {s.govde}
                </pre>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-[var(--ink-faint)]">
                    {s.kaynak === 'panel'
                      ? `Panelden düzenlendi · ${s.guncellemeZamani ? tarihSaatYaz(s.guncellemeZamani) : ''}`
                      : 'Varsayılan metin'}
                  </span>
                  <Dugme gorunum="ikincil" boy="sm" onClick={() => setAcik(s)}>
                    {duzenleyebilir ? 'Düzenle' : 'Görüntüle'}
                  </Dugme>
                </div>
              </KartGovdesi>
            </Kart>
          ))}
        </div>
      )}

      {acik && <SablonEditoru sablon={acik} duzenleyebilir={duzenleyebilir} onKapat={() => setAcik(null)} />}
    </Kabuk>
  );
}

function SablonEditoru({
  sablon,
  duzenleyebilir,
  onKapat,
}: {
  sablon: Sablon;
  duzenleyebilir: boolean;
  onKapat: () => void;
}) {
  const kuyruk = useQueryClient();
  const [konu, setKonu] = useState(sablon.konu ?? '');
  const [govde, setGovde] = useState(sablon.govde);
  const [numara, setNumara] = useState('');
  const [onizleme, setOnizleme] = useState<SablonOnizleme | null>(null);
  const govdeRef = useRef<HTMLTextAreaElement>(null);

  const degisti = konu !== (sablon.konu ?? '') || govde !== sablon.govde;

  // Önizleme: 300 ms gecikmeli — her tuşta istek atılmasın.
  useEffect(() => {
    const zamanlayici = window.setTimeout(() => {
      api
        .sablonOnizle(sablon.key, { konu, govde })
        .then(setOnizleme)
        .catch(() => setOnizleme(null));
    }, 300);
    return () => window.clearTimeout(zamanlayici);
  }, [sablon.key, konu, govde]);

  const kaydet = useMutation({
    mutationFn: () => api.sablonKaydet(sablon.key, { konu, govde }),
    onSuccess: () => {
      void kuyruk.invalidateQueries({ queryKey: ['sablonlar'] });
      basariBildir('Şablon kaydedildi.');
      onKapat();
    },
    onError: hataBildir,
  });

  const sifirla = useMutation({
    mutationFn: () => api.sablonSifirla(sablon.key),
    onSuccess: () => {
      void kuyruk.invalidateQueries({ queryKey: ['sablonlar'] });
      basariBildir('Şablon varsayılan metne döndürüldü.');
      onKapat();
    },
    onError: hataBildir,
  });

  const testGonder = useMutation({
    mutationFn: () => api.sablonTestGonder(sablon.key, sablon.kanal === 'SMS' ? { numara } : {}),
    onSuccess: (s) => {
      if (s.ok) basariBildir(`${s.alici} adresine gönderildi.`, 'Test gönderildi');
      else bilgiBildir(`${s.durumEtiketi}: ${s.mesaj}`, 'Test gönderilemedi');
    },
    onError: hataBildir,
  });

  const yerTutucuEkle = (ad: string) => {
    const alan = govdeRef.current;
    const parca = `{{${ad}}}`;
    if (!alan) {
      setGovde((g) => g + parca);
      return;
    }
    const bas = alan.selectionStart ?? govde.length;
    const bit = alan.selectionEnd ?? govde.length;
    const yeni = govde.slice(0, bas) + parca + govde.slice(bit);
    setGovde(yeni);
    requestAnimationFrame(() => {
      alan.focus();
      alan.setSelectionRange(bas + parca.length, bas + parca.length);
    });
  };

  const sorunlar = useMemo(() => onizleme?.problemler ?? [], [onizleme]);

  return (
    <Pencere
      acik
      genis
      baslik={sablon.ad}
      aciklama={sablon.aciklama}
      basliksag={
        <DurumRozeti
          ton={sablon.kanal === 'MAIL' ? 'info' : 'neutral'}
          etiket={sablon.kanal === 'MAIL' ? 'E-posta' : 'SMS'}
        />
      }
      onKapat={onKapat}
      altCubuk={
        duzenleyebilir ? (
          <>
            <Dugme
              gorunum="birincil"
              ikon={<Save size={15} />}
              bekliyor={kaydet.isPending}
              disabled={!degisti || sorunlar.length > 0}
              onClick={() => kaydet.mutate()}
            >
              Kaydet
            </Dugme>
            <Dugme
              gorunum="ikincil"
              ikon={<Send size={15} />}
              bekliyor={testGonder.isPending}
              disabled={degisti}
              title={degisti ? 'Önce kaydedin — test, kayıtlı metni gönderir' : undefined}
              onClick={() => testGonder.mutate()}
            >
              {sablon.kanal === 'MAIL' ? 'Kendime test e-postası' : 'Test SMS gönder'}
            </Dugme>
            {sablon.kaynak === 'panel' && (
              <Dugme
                gorunum="sessiz"
                ikon={<RotateCcw size={15} />}
                bekliyor={sifirla.isPending}
                onClick={async () => {
                  const onay = await onayIste({
                    baslik: 'Varsayılana dönülsün mü?',
                    mesaj: 'Panelden yapılan düzenleme silinir; kod içindeki varsayılan metin geçerli olur.',
                    onayMetni: 'Varsayılana dön',
                  });
                  if (onay) sifirla.mutate();
                }}
              >
                Varsayılana dön
              </Dugme>
            )}
            <Dugme gorunum="ikincil" onClick={onKapat} className="ml-auto">
              Vazgeç
            </Dugme>
          </>
        ) : (
          <Dugme gorunum="ikincil" onClick={onKapat}>
            Kapat
          </Dugme>
        )
      }
    >
      <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2">
        {/* ---- Düzenleme ---- */}
        <div className="flex flex-col gap-4">
          {sablon.kanal === 'MAIL' && (
            <Girdi
              etiket="Konu"
              value={konu}
              onChange={(o) => setKonu(o.target.value)}
              disabled={!duzenleyebilir}
            />
          )}
          <CokSatir
            ref={govdeRef}
            etiket="Metin"
            rows={sablon.kanal === 'SMS' ? 5 : 12}
            value={govde}
            onChange={(o) => setGovde(o.target.value)}
            disabled={!duzenleyebilir}
            ipucu={
              sablon.kanal === 'SMS'
                ? 'Türkçe karakter (ş, ğ, ı, ç, ö, ü) tek SMS sınırını 160’dan 70’e düşürür.'
                : 'Düz metin yazın; e-postada satır sonları korunur, HTML gerekmez.'
            }
          />
          <div className="flex flex-col gap-2">
            <span className="ust-etiket">Yer tutucular — tıklayınca imlece eklenir</span>
            <div className="flex flex-wrap gap-2">
              {sablon.alanlar.map((a) => (
                <button
                  key={a.ad}
                  type="button"
                  disabled={!duzenleyebilir}
                  title={a.aciklama}
                  onClick={() => yerTutucuEkle(a.ad)}
                  className="rakam rounded-full border border-[var(--line-strong)] px-3 py-1 text-xs text-[var(--ink)] hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {`{{${a.ad}}}`}
                </button>
              ))}
            </div>
          </div>
          {sablon.kanal === 'SMS' && duzenleyebilir && (
            <Girdi
              etiket="Test için cep numaranız"
              inputMode="tel"
              placeholder="05XX XXX XX XX"
              value={numara}
              onChange={(o) => setNumara(o.target.value)}
              ipucu="Yalnızca test gönderimi için; kaydedilmez."
            />
          )}
        </div>

        {/* ---- Önizleme ---- */}
        <div className="flex flex-col gap-3">
          <span className="ust-etiket">Önizleme — örnek veriyle</span>
          {sorunlar.length > 0 && (
            <div
              role="alert"
              className="rounded-[var(--radius-md)] border border-[var(--state-bad)] bg-[var(--state-bad-soft)] p-3 text-sm text-[var(--ink)]"
            >
              {sorunlar.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </div>
          )}
          {onizleme?.kanal === 'MAIL' && (
            <div className="rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)]">
              <div className="border-b border-[var(--line)] px-4 py-3">
                <span className="text-xs text-[var(--ink-faint)]">Konu</span>
                <p className="text-sm font-medium text-[var(--ink)]">{onizleme.konu}</p>
              </div>
              <pre className="p-4 text-sm whitespace-pre-wrap text-[var(--ink)]">{onizleme.metin}</pre>
            </div>
          )}
          {onizleme?.kanal === 'SMS' && onizleme.sms && (
            <div className="flex flex-col gap-2">
              <div className="mx-auto w-full max-w-xs rounded-[var(--radius-lg)] bg-[var(--surface-sunken)] p-4">
                <div className="inline-flex items-start gap-2">
                  <MessageSquare
                    size={16}
                    className="mt-0.5 shrink-0 text-[var(--ink-faint)]"
                    aria-hidden="true"
                  />
                  <p className="text-sm whitespace-pre-wrap text-[var(--ink)]">{onizleme.metin}</p>
                </div>
              </div>
              <p className="rakam text-center text-xs text-[var(--ink-muted)]">
                {onizleme.sms.uzunluk} karakter ·{' '}
                {onizleme.sms.alfabe === 'UCS2' ? 'Türkçe karakter var' : 'Latin karakter'} ·{' '}
                <strong
                  className={onizleme.sms.parca > 1 ? 'text-[var(--state-wait)]' : 'text-[var(--state-ok)]'}
                >
                  {onizleme.sms.parca} SMS
                </strong>{' '}
                (sınır {onizleme.sms.sinir}/parça)
              </p>
            </div>
          )}
          {!onizleme && <Yukleniyor satir={3} />}
          {sablon.kanal === 'MAIL' && (
            <p className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-faint)]">
              <Mail size={12} aria-hidden="true" />
              E-postada müşteri adı gibi değerler otomatik güvenli hâle getirilir (HTML çalışmaz).
            </p>
          )}
        </div>
      </div>
    </Pencere>
  );
}
